import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Settings, Save, Building2, Loader2 } from "lucide-react";

const VENUE_TYPES = {
  restaurant: "Restaurant",
  bar: "Bar",
  cafeteria: "Cafeteria",
  hotel: "Hotel",
  other: "Altres",
};

export default function VenueSettings() {
  const { currentVenue, userRole, loading: venueLoading, refreshData } = useVenue();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (currentVenue) {
      setForm({
        name: currentVenue.name || "",
        address: currentVenue.address || "",
        city: currentVenue.city || "",
        phone: currentVenue.phone || "",
        email: currentVenue.email || "",
        venue_type: currentVenue.venue_type || "restaurant",
      });
    }
  }, [currentVenue]);

  const handleSave = async () => {
    setSaving(true);
    await hosys.entities.Venue.update(currentVenue.id, form);
    await refreshData();
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (venueLoading) return <QuickLoader />;

  if (!currentVenue) {
    return (
      <EmptyState
        icon={Settings}
        title="Cap local seleccionat"
        description="Selecciona o crea un local primer."
      />
    );
  }

  const isEditable = userRole === "superadmin" || userRole === "admin";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Configuració del Local"
        subtitle={currentVenue.name}
      />

      <Card className="p-6 border-0 shadow-sm">
        <div className="space-y-5 max-w-xl">
          <div>
            <Label>Nom del local</Label>
            <Input
              value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!isEditable}
            />
          </div>
          <div>
            <Label>Tipus d'establiment</Label>
            <Select
              value={form.venue_type || "restaurant"}
              onValueChange={(v) => setForm({ ...form, venue_type: v })}
              disabled={!isEditable}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(VENUE_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Adreça</Label>
            <Input
              value={form.address || ""}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              disabled={!isEditable}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ciutat</Label>
              <Input
                value={form.city || ""}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                disabled={!isEditable}
              />
            </div>
            <div>
              <Label>Telèfon</Label>
              <Input
                value={form.phone || ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                disabled={!isEditable}
              />
            </div>
          </div>
          <div>
            <Label>Email de contacte</Label>
            <Input
              type="email"
              value={form.email || ""}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={!isEditable}
            />
          </div>

          {isEditable && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saved ? "Guardat ✓" : "Guardar canvis"}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}